import { createContext, useState, useCallback, ReactNode } from 'react';

interface AbortControllerContextValueType {
    controllers: Map<string, AbortController>;
    // eslint-disable-next-line no-unused-vars
    addAbortController: (key: string) => AbortController;
    // eslint-disable-next-line no-unused-vars
    removeController: (key: string) => void;
    // eslint-disable-next-line no-unused-vars
    replaceControllerKey: (oldKey: string, newKey: string) => void;
    abortAll: () => void
}

const AbortControllerContext = createContext<AbortControllerContextValueType>({
    controllers: new Map(),
    addAbortController: () => new AbortController(),
    removeController: () => {},
    replaceControllerKey: () => {},
    abortAll: () => {}
});

const AbortControllerProvider = ({ children }: { children: ReactNode }) => {
    const [ controllers, setControllers ] = useState<Map<string, AbortController>>(new Map());

    const addAbortController = useCallback((key: string) => {
        const controller = new AbortController();
        setControllers((prev) => new Map(prev).set(key, controller));
        return controller;
    }, []);

    const removeController = (key: string) => {
        setControllers((prev) => {
            const newControllers = new Map(prev);
            newControllers.delete(key);
            return newControllers;
        });
    };

    const replaceControllerKey = (oldKey: string, newKey: string) => {
        setControllers((prev) => {
            const newControllers = new Map(prev);
            if (newControllers.has(oldKey)) {
                const value = newControllers.get(oldKey) as AbortController;
                newControllers.delete(oldKey);
                newControllers.set(newKey, value);
            } else {
                console.error(`Key "${oldKey}" does not exist in the map.`);
            }

            return newControllers;
        });
    };

    const abortAll = useCallback(() => {
        controllers.forEach((controller) => controller.abort());
        setControllers(new Map());
    }, [ controllers ]);

    return (
        <AbortControllerContext.Provider
            value={{
                controllers,
                addAbortController,
                replaceControllerKey,
                removeController,
                abortAll
            }}
        >
            {children}
        </AbortControllerContext.Provider>
    );
};

export { AbortControllerContext, AbortControllerProvider };
